import { useMutation } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { MutationConfig, queryClient } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

import { emailEntriesKeys } from './getEmailEntries';
import { emailEntryKeys } from './getEmailEntry';
import { emailThreadAttachmentsKeys } from './getEmailThreadAttachments';

import { entryActionCustomValidateOptions } from '../../../../api/inbox';
import { EmailDeliveryType } from '../../../../types';
import { inboxEntriesKeys } from '../../../Sidebar/api/getInboxEntries';

// const url = `${PREFIX_URL}/v01/email`;
const url = `${PREFIX_URL}/inbox-threads/:inboxEntryId/send-email`;

type CreateReplyResponse = {
  data: any;
  status: boolean;
};

export type CreateReplyDTO = {
  inboxEntryId: number;
  params: {
    to: string[];
    cc: string[];
    html: string;
    raw: string;
    files: {
      filename: string;
      origin: string;
      size: number;
      id?: string;
      cid?: string;
    }[];
    'email-id': number;
  };
};

export const createReply = ({
  inboxEntryId,
  params,
}: CreateReplyDTO): Promise<CreateReplyResponse> => {
  return axios.post(
    generateRoutePath(url, { inboxEntryId }),
    { ...params },
    {
      ...entryActionCustomValidateOptions,
    }
  );
};

type useCreateReplyOptions = {
  projectId: string;
  inboxEntryId: number;
  config?: MutationConfig<typeof createReply>;
};

export const useCreateReply = ({ projectId, inboxEntryId, config }: useCreateReplyOptions) => {
  return useMutation({
    onSuccess: (data) => {
      const newReply = {
        ...data.data,
        isLatest: true,
        isExpanded: true,
      };

      // If we require the initial latest to not be hidden, we have to set the last item of page.data.emails to be isLatest and isExpanded to true
      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              const newEmailEntries = [
                {
                  ...newReply,
                  isLatest: true,
                  isSelected: true,
                },
                ...page.data.emails,
              ];

              return {
                ...page,
                data: {
                  ...page.data,
                  'total-count': newEmailEntries.length,
                  emails: newEmailEntries,
                },
              };
            }),
          };
        }
      );

      // Manually sync new email
      queryClient.setQueriesData([...inboxEntriesKeys.lists({ projectId })], (prev: any) => {
        return {
          ...prev,
          pages: prev.pages.map((page) => {
            return {
              ...page,
              data: page.data.map((inboxEntry) => {
                if (inboxEntry['source-id'] === inboxEntryId) {
                  return {
                    ...inboxEntry,
                    thread: {
                      ...inboxEntry.thread,
                      text: data.data.text,
                      'open-inbound-emails-count':
                        inboxEntry.thread['open-inbound-emails-count'] + 1,
                      'total-emails-count': inboxEntry.thread['total-emails-count'] + 1,
                      'open-inbound-attachments-count':
                        inboxEntry.thread['open-inbound-attachments-count'] +
                        data.data.attachments.length,
                      attachments: [...inboxEntry.thread.attachments, ...data.data.attachments],
                    },
                  };
                }

                return inboxEntry;
              }),
            };
          }),
        };
      });

      // Prefill new expanded email
      queryClient.setQueryData(
        [...emailEntryKeys.detail({ emailId: newReply.id, inboxEntryId, projectId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...newReply,
            },
          };
        }
      );

      // If there are files, invalidate the attachments tab
      if (data.data.attachments.length) {
        queryClient.invalidateQueries([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ]);
      }
    },
    ...config,
    mutationFn: createReply,
  });
};
